<template>
    <DsPopover
        hide-arrow
        is-open
        class="inline-add-popover-menu"
        :style="popoverStyle"
    >
        <template #default>
            <div class="inline-add-popover-menu-content">
                <DsInputField
                    ref="searchInput"
                    v-model="searchText"
                    :label="searchPlaceholder"
                    class="element-search"
                >
                    <template #leading>
                        <DsIcon name="search" />
                    </template>
                </DsInputField>
                <div class="element-list">
                    <DsListItem
                        v-for="element in filteredElements"
                        :key="element.id"
                        :title="element.displayLabel"
                        class="element-list-item"
                        @click="onElementClick(element)"
                    >
                        <template #leading>
                            <img class="element-icon" :src="element.iconUrl" />
                        </template>
                    </DsListItem>
                </div>
            </div>
        </template>
    </DsPopover>
</template>

<script>
import { getIconPathByCellType } from '../utils/cell/automation-display-utils';
import {
    CELL_TYPE,
    WHEN_CELL_OPTIONS,
    THEN_CELL_OPTIONS,
    INTEGRATION_TRIGGER_TYPE,
    INTEGRATION_ACTION_TYPE,
    MAIN_CANVAS_ID,
    GRID_SIZE,
    PROCESS_EMAIL_CONFIRM, SEQUENCE_START,
} from '../utils/editor.constants';

export default {
    components: {},
    props: {
        selectedSequence: {
            type: String,
            required: true,
        },

        inlineAddData: {
            type: Object,
            required: true,
        },

        enabledCellTypes: {
            type: Array,
            required: true,
        },

        enabledIntegrations: {
            type: Array,
            default: () => [],
        },
    },

    emits: ['add-cell'],

    data() {
        return {
            searchText: '',
        };
    },

    computed: {
        sourceElement() {
            return this.inlineAddData.sourceElement;
        },

        type() {
            return this.inlineAddData.type;
        },

        event() {
            return this.inlineAddData.event;
        },

        internalZoomLevel() {
            return this.inlineAddData.internalZoomLevel;
        },

        showLabels() {
            return this.inlineAddData.showLabels;
        },

        stagePan() {
            return this.inlineAddData.stagePan;
        },

        searchPlaceholder() {
            return this.type === CELL_TYPE.WHEN
                ? this.$t('funnel.inlineAddMenu.searchPlaceholderWhen')
                : this.$t('funnel.inlineAddMenu.searchPlaceholderThen');
        },

        filteredElements() {
            const categorizedCells = this.type === CELL_TYPE.WHEN
                ? WHEN_CELL_OPTIONS
                : THEN_CELL_OPTIONS;

            const enabledCells = categorizedCells.filter((cell) => (this.enabledCellTypes.includes(cell.name)))
                .map((cell) => ({
                    ...cell,
                    displayLabel: this.$t(`funnel.sidebar.${this.type}.cell.${cell.name}.label`),
                    isIntegration: false,
                }));

            const integrationCells = this.enabledIntegrations.filter((integration) => {
                return integration.sidebarType.toUpperCase() === this.type.toUpperCase();
            }).map((integration) => {
                return {
                    ...integration,
                    displayLabel: integration.label,
                    isIntegration: true,
                };
            });

            const elements = [...enabledCells, ...integrationCells];

            const isInSequence = this.selectedSequence !== MAIN_CANVAS_ID;
            const iconColor = this.type === CELL_TYPE.WHEN ? 'green' : 'blue';

            const searchWords = this.searchText.toLowerCase().trim().split(' ');

            return elements.filter((element) => {
                if (isInSequence && element.name === PROCESS_EMAIL_CONFIRM || element.name === SEQUENCE_START) return false;
                if (!this.searchText) return true;

                const searchString = `${element.name} ${element.type} ${this.$t(`funnel.sidebar.${this.type}.cell.${element.name}.label`)}`;
                const cellNameWords = searchString.toLowerCase().trim().split(' ');

                return searchWords.every((searchWord) => cellNameWords.some((cellNameWord) => cellNameWord.includes(searchWord)));
            }).map((element) => {
                return {
                    ...element,
                    displayLabel: element.label || this.$t(`funnel.sidebar.${this.type}.cell.${element.name}.label`),
                    iconUrl: element.iconUrl || getIconPathByCellType(element.name, iconColor),
                };
            });
        },

        popoverStyle() {
            const {
                event,
                sourceElement,
                internalZoomLevel,
                stagePan,
                showLabels,
            } = this;
            const container = event?.srcElement?.getBoundingClientRect();

            if (sourceElement) {
                const elementX = sourceElement?.mxGeometry?.x;
                const elementY = sourceElement?.mxGeometry?.y;

                const groupOffsetX = showLabels ? 333 : 350;
                const groupOffsetY = showLabels ? -10 : -60;


                const x = (elementX + container.left) * internalZoomLevel + stagePan.x + groupOffsetX;
                const y = (elementY + container.top) * internalZoomLevel + stagePan.y + groupOffsetY;

                return {
                    zIndex: 50000,
                    transform: `translate(${x}px, ${y}px)`,
                };
            }

            const x = container.left + (container.width / 2);
            const y = container.top + (container.height / 2) - 45;

            return {
                zIndex: 50000,
                transform: `translate(${x}px, ${y}px)`,
            };
        },
    },

    mounted() {
        this.$nextTick(() => this.$refs.searchInput.$el.querySelector('input').focus());
    },

    methods: {
        onElementClick(element) {
            const addOffset = GRID_SIZE * 5;

            const newElement = {
                cellName: element.name,
                cellDefaultName: element.defaultName ? this.$t(element.defaultName) : this.$t(`funnel.sidebar.${this.type}.cell.${element.label || element.name}.label`),
                parent: this.selectedSequence,
                x: this.sourceElement
                    ? this.sourceElement.mxGeometry.x + addOffset
                    : GRID_SIZE * 3,
                y: this.sourceElement
                    ? this.sourceElement.mxGeometry.y
                    : GRID_SIZE * 2,
                autoOpenSequence: true,
                autoCreateSequence: this.type === CELL_TYPE.THEN && this.selectedSequence === MAIN_CANVAS_ID,
                autoCreateEdge: !!this.sourceElement,
                edgeSource: this.sourceElement,
                value: {},
            };

            if (element.metaType === INTEGRATION_TRIGGER_TYPE) {
                newElement.value.integrationTriggerId = element.id;
                newElement.cellDefaultName = element.label;
                newElement.cellName = INTEGRATION_TRIGGER_TYPE;
            }

            if (element.metaType === INTEGRATION_ACTION_TYPE) {
                newElement.value.integrationActionId = element.id;
                newElement.cellDefaultName = element.label;
                newElement.cellName = INTEGRATION_ACTION_TYPE;
            }

            this.$emit('add-cell', newElement);
        },
    },
};
</script>

<style scoped lang="scss">
.element-list {
    padding-left: $spacing-125;
    max-height: px-to-rem(300px);
    overflow-y: auto;
    overflow-x: hidden;
}
.element-icon {
    width: 24px;
    max-height: 24px;
}

.element-list-item {
    --list-item-padding: 0;
    padding-left: $spacing-100;
    cursor: pointer;
    border-radius: $border-radius;

    &:hover {
        background-color: $color-gray-100;
    }
}

.inline-add-popover-menu ::v-deep .popover, .inline-add-popover-menu-content {
    width: 300px;
}

.element-search {
    margin: 0;
    border-bottom: 1px solid $color-gray-300;
    --input-border: 0;
}

// css bleed reset
.inline-add-popover-menu ::v-deep {
    .list-item-info {
        border: none;
        padding: 0;
    }

    .popover .title {
        background: none;
        border: none;
        padding: $spacing-200 0;
    }
}

</style>
